import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Divider,
  AppBar,
  Toolbar,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import { Upload, Preview, Download, AccountBalance } from "@mui/icons-material";

import FileUpload from "./components/FileUpload";
import TransactionPreview from "./components/TransactionPreview";
import { MT940Parser } from "./utils/mt940Parser";
import { STAConverter } from "./utils/staConverter";
import "./fonts/fonts.css";
import "./styles/typography.css";

const theme = createTheme({
  palette: {
    primary: {
      main: "#afca0b", // ZHP Brand Green
      light: "#c6d93f",
      dark: "#7a9007",
    },
    secondary: {
      main: "#f57c00", // Orange accent
      light: "#ff9800",
      dark: "#e65100",
    },
    background: {
      default: "#fafafa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2e2e2e",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily:
      "'Museo', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    h1: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 700,
    },
    h2: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 700,
    },
    h4: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 700,
    },
    h5: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
    },
    h6: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
    },
    body1: {
      fontFamily: "'Museo Sans', sans-serif",
      fontWeight: 500,
    },
    body2: {
      fontFamily: "'Museo Sans', sans-serif",
      fontWeight: 500,
    },
    button: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
      textTransform: "none", // Zachowaj naturalne wielkości liter
    },
    caption: {
      fontFamily: "'Museo Sans', sans-serif",
      fontWeight: 500,
    },
    overline: {
      fontFamily: "'Museo', sans-serif",
      fontWeight: 500,
    },
  },
});

const steps = [
  {
    label: "Wgraj plik MT940",
    description: "Wybierz plik MT940 z wyciągiem bankowym",
    icon: <Upload />,
  },
  {
    label: "Podgląd danych",
    description: "Sprawdź i zweryfikuj przeanalizowane transakcje",
    icon: <Preview />,
  },
  {
    label: "Pobierz wynik",
    description: "Wygeneruj i pobierz plik w formacie programu księgowego",
    icon: <Download />,
  },
];

function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [parsedStatements, setParsedStatements] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = (data) => {
    setError(null);

    if (!data) {
      setFileData(null);
      setParsedStatements(null);
      setActiveStep(0);
      return;
    }

    setFileData(data);
    setIsProcessing(true);

    try {
      // Parsuj plik MT940
      const statements = MT940Parser.parse(data.content);

      if (!statements || statements.length === 0) {
        throw new Error("Nie znaleziono prawidłowych danych MT940 w pliku");
      }

      setParsedStatements(statements);
      setActiveStep(1);
    } catch (err) {
      setError(`Błąd podczas analizowania pliku: ${err.message}`);
      setFileData(null);
      setParsedStatements(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!parsedStatements) {
      setError("Brak danych do konwersji");
      return;
    }

    try {
      setIsProcessing(true);

      // Konwertuj do formatu STA
      const staContent = STAConverter.convertToSTA(parsedStatements);

      // Dodaj BOM dla UTF-8 aby zapewnić prawidłowe kodowanie polskich znaków
      const BOM = "\uFEFF";
      const contentWithBOM = BOM + staContent;

      // Utwórz i pobierz plik z UTF-8 BOM
      const blob = new Blob([contentWithBOM], {
        type: "text/plain;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = STAConverter.generateFileName(parsedStatements);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setActiveStep(2);
    } catch (err) {
      setError(`Błąd podczas konwersji: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFileData(null);
    setParsedStatements(null);
    setError(null);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <FileUpload
            onFileSelect={handleFileSelect}
            acceptedFiles={[".txt"]}
            maxSize={10 * 1024 * 1024} // 10MB
          />
        );
      case 1:
        return (
          <Box>
            <TransactionPreview
              statements={parsedStatements}
              fileContent={fileData?.content}
            />
            <Box mt={3} display="flex" gap={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isProcessing}
              >
                Wgraj inny plik
              </Button>
              <Button
                variant="contained"
                onClick={handleDownload}
                disabled={isProcessing}
                startIcon={<Download />}
              >
                {isProcessing ? "Generowanie..." : "Pobierz plik .sta"}
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box textAlign="center">
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Konwersja zakończona pomyślnie!
              </Typography>
              <Typography variant="body2">
                Plik został pobrany na Twój komputer. Możesz teraz zaimportować
                go do programu księgowego.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={handleReset}
              startIcon={<Upload />}
            >
              Konwertuj kolejny plik
            </Button>
          </Box>
        );
      default:
        return "Nieznany krok";
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <AccountBalance sx={{ mr: 2, color: "white" }} />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, color: "white" }}
            className="museo-heading museo-heading--medium"
          >
            Szmal ZHP - Konwerter MT940
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              gutterBottom
              className="museo-heading museo-heading--large"
            >
              Konwersja plików MT940
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              className="museo-body"
            >
              Konwertuj pliki MT940 z banku do formatu obsługiwanego przez
              program księgowy
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={step.icon}
                  optional={
                    <Typography variant="caption">
                      {step.description}
                    </Typography>
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Divider sx={{ mb: 4 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box>{getStepContent(activeStep)}</Box>

          {fileData && activeStep === 1 && (
            <Box mt={4} p={3} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="h6" gutterBottom>
                Informacje o pliku
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Nazwa:</strong> {fileData.name}
                <br />
                <strong>Rozmiar:</strong> {(fileData.size / 1024).toFixed(1)} KB
                <br />
                <strong>Liczba wyciągów:</strong>{" "}
                {parsedStatements?.length || 0}
                <br />
                <strong>Liczba transakcji:</strong>{" "}
                {parsedStatements?.reduce(
                  (total, statement) => total + statement.transactions.length,
                  0
                ) || 0}
              </Typography>
            </Box>
          )}
        </Paper>

        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Aplikacja konwertuje pliki MT940 na format kompatybilny z programami
            księgowymi. Dane są przetwarzane lokalnie w przeglądarce i nie są
            wysyłane na serwer.
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
